import React, { useEffect, useState, useRef } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useShop } from "../context/ShopContext";
import { useAuth } from "../components/AuthContext";
import apiClient from "../apiClient";

const BG      = "#0f172a";
const CARD    = "#1a2035";
const WINE    = "#7B1D4F";
const TEXT    = "#CBD5E1";
const MUTED   = "#64748B";
const BORDER  = "rgba(255,255,255,0.08)";

const SignInPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]      = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { setShopId, setToken, setshopName, setuserId, setuserName, setcompanyId, setGroupId } = useShop();
  const { isAuthenticated, login } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      const lastVisited = localStorage.getItem("lastVisitedPath") || "/home";
      navigate(lastVisited, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const showToast = (message, type = "error") => {
    toast.dismiss();
    type === "error" ? toast.error(message) : toast.success(message);
  };

  const performLogin = async (user, pass) => {
    setLoading(true);
    try {
      const response = await apiClient.post(
        "/Login/GetUserLogin",
        { userName: user, password: pass },
        { headers: { "Content-Type": "application/json", accept: "*/*" } }
      );

      if (response.status === 200 && response.data?.data) {
        const { shopId, tokenString, shopName, userId, userName, companyId, groupId } = response.data.data;

        login(tokenString);
        setShopId(shopId);
        setToken(tokenString);
        setshopName(shopName);
        setuserId(userId);
        setuserName(userName);
        setcompanyId(companyId);
        setGroupId(groupId);

        localStorage.setItem("shopId", shopId);
        localStorage.setItem("tokenString", tokenString);
        localStorage.setItem("shopName", shopName);
        localStorage.setItem("userId", userId);
        localStorage.setItem("userName", userName);
        localStorage.setItem("companyId", companyId);
        localStorage.setItem("groupId", groupId);

        showToast("Login successful!", "success");
        navigate("/home");
      } else {
        showToast(response.data?.message || "Invalid username and password.");
      }
    } catch (err) {
      if (err?.response?.status !== 401 && err?.response?.status !== 403) {
        const msg = err?.response?.data?.message || err?.response?.data?.Message || err?.message || "Something went wrong. Please try again.";
        showToast(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-login from URL: #/signin?uid=demo&pwd=demo123
  // Reads directly from window.location.hash — works reliably with HashRouter
  useEffect(() => {
    if (isAuthenticated) return;

    // window.location.hash = "#/signin?uid=demo&pwd=demo123"
    const hash = window.location.hash;                     // "#/signin?uid=demo&pwd=demo123"
    const qIndex = hash.indexOf("?");
    if (qIndex === -1) return;                             // no query string in URL

    const params = new URLSearchParams(hash.slice(qIndex)); // "?uid=demo&pwd=demo123"
    const uid    = params.get("uid");
    const rawPwd = params.get("pwd");

    if (!uid || !rawPwd) return;

    setUsername(uid);
    setPassword(rawPwd);
    performLogin(uid, rawPwd);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      showToast(
        !username.trim() && !password.trim()
          ? "Please enter username and password."
          : !username.trim()
          ? "Please enter username."
          : "Please enter password."
      );
      return;
    }

    performLogin(username, password);
  };

  return (
    <div style={{
      minHeight: "100vh", background: BG,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Inter', sans-serif", padding: "24px",
    }}>
      <ToastContainer theme="dark" />

      <div style={{
        width: "100%", maxWidth: 420,
        background: CARD, borderRadius: 20,
        border: `1px solid ${BORDER}`,
        boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
        padding: "40px 36px",
      }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 60, height: 60, borderRadius: 16, margin: "0 auto 14px",
            background: "linear-gradient(135deg,#3D0D26,#5E1540,#7B1D4F)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 28, boxShadow: "0 8px 24px rgba(123,29,79,0.45)",
          }}>🍷</div>
          <div style={{ fontWeight: 800, fontSize: 22, color: "#fff", letterSpacing: 0.5 }}>SIPSTOCKS</div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>Sign in to your account</div>
        </div>

        <form onSubmit={handleSubmit} noValidate>

          {/* Username */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 6, letterSpacing: 0.4 }}>
              USERNAME
            </label>
            <input
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              style={{
                width: "100%", boxSizing: "border-box",
                background: "rgba(255,255,255,0.05)",
                border: `1px solid ${BORDER}`,
                borderRadius: 10, padding: "12px 14px",
                color: "#fff", fontSize: 14,
                outline: "none", transition: "border-color 0.2s",
              }}
              onFocus={(e) => e.target.style.borderColor = WINE}
              onBlur={(e) => e.target.style.borderColor = BORDER}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 28 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 6, letterSpacing: 0.4 }}>
              PASSWORD
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                style={{
                  width: "100%", boxSizing: "border-box",
                  background: "rgba(255,255,255,0.05)",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 10, padding: "12px 42px 12px 14px",
                  color: "#fff", fontSize: 14,
                  outline: "none", transition: "border-color 0.2s",
                }}
                onFocus={(e) => e.target.style.borderColor = WINE}
                onBlur={(e) => e.target.style.borderColor = BORDER}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={{
                  position: "absolute", right: 13, top: "50%",
                  transform: "translateY(-50%)",
                  background: "none", border: "none",
                  color: MUTED, cursor: "pointer", fontSize: 16,
                  display: "flex", alignItems: "center", padding: 0,
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = TEXT}
                onMouseLeave={(e) => e.currentTarget.style.color = MUTED}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%", padding: "13px 0",
              background: loading ? "rgba(123,29,79,0.45)" : "linear-gradient(135deg,#3D0D26,#5E1540,#7B1D4F)",
              border: "none", borderRadius: 10,
              color: "#fff", fontSize: 15, fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : "0 4px 16px rgba(123,29,79,0.45)",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.opacity = "0.9"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        {/* Footer */}
        <div style={{ marginTop: 28, textAlign: "center", fontSize: 12, color: MUTED }}>
          Manage your wine collections with ease
        </div>
      </div>

      <style>{`
        input::placeholder { color: #475569; }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 100px #1a2035 inset !important;
          -webkit-text-fill-color: #fff !important;
        }
      `}</style>
    </div>
  );
};

export default SignInPage;
